import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Space, Modal, Form, Input, Select,
  Popconfirm, message, Tag, InputNumber, Badge, Typography, Row, Col, Statistic, Tooltip, ColorPicker,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined,
  EnvironmentOutlined, TagsOutlined, UserOutlined, ReloadOutlined,
  CarOutlined, TeamOutlined, HolderOutlined,
} from '@ant-design/icons';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { masterAPI } from '../services/api';
import { brand, semantic, modalWidth, gray } from '../theme/tokens';
import { EmptyState } from '../components/common';

const { Option } = Select;

// ── 타입 정의 ──
interface Company { id: number; name: string; code: string; contact_name: string; contact_phone: string; contact_email: string; address: string; note: string; is_active: number; }
interface Location { id: number; region: string; city: string; full_name: string; detail_address: string; note: string; is_active: number; }
interface Category { id: number; name: string; description: string; sort_order: number; is_active: number; }
interface FieldOwner { id: number; name: string; phone: string; address: string; location_id: number | null; location_name: string; note: string; is_active: number; }
interface Vehicle { id: number; vehicle_number: string; vehicle_type: string; model: string; owner: string; note: string; is_active: boolean; }
interface Researcher { id: number; name: string; position: string; phone: string; email: string; department: string; note: string; is_active: boolean; sort_order?: number; }

// ── 의뢰사 탭 ──
const CompanyTab: React.FC = () => {
  const [data, setData] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.companies.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.companies.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.companies.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.companies.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">총 {data.length}개사</Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>의뢰사 등록</Button>
        </Space>
      </div>
      <Table dataSource={data} rowKey="id" size="small" loading={loading} pagination={false} showSorterTooltip={false}
        locale={{ emptyText: <EmptyState title="등록된 의뢰사가 없습니다" size="sm" /> }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 50, sorter: (a: Company, b: Company) => a.id - b.id },
          { title: '의뢰사명', dataIndex: 'name', width: 120, sorter: (a: Company, b: Company) => a.name.localeCompare(b.name, 'ko'), render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
          { title: '코드', dataIndex: 'code', width: 80, sorter: (a: Company, b: Company) => (a.code || '').localeCompare(b.code || '') },
          { title: '담당자', dataIndex: 'contact_name', width: 100, sorter: (a: Company, b: Company) => (a.contact_name || '').localeCompare(b.contact_name || '', 'ko') },
          { title: '연락처', dataIndex: 'contact_phone', width: 120 },
          { title: '이메일', dataIndex: 'contact_email', width: 180 },
          { title: '상태', dataIndex: 'is_active', width: 70, sorter: (a: Company, b: Company) => a.is_active - b.is_active, render: (v: number) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
          { title: '비고', dataIndex: 'note', ellipsis: true },
          { title: '', width: 80, render: (_: any, r: Company) => (
            <Space size={4}>
              <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
              <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
                <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
          )},
        ]}
      />
      <Modal title={editingId ? '의뢰사 수정' : '의뢰사 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.md} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="의뢰사명" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="code" label="코드/약어"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="contact_name" label="담당자명"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="contact_phone" label="연락처"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="contact_email" label="이메일"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="address" label="주소"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={18}><Form.Item name="note" label="비고"><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="is_active" label="상태" initialValue={1}><Select><Option value={1}>활성</Option><Option value={0}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 시험장소 탭 ──
const LocationTab: React.FC = () => {
  const [data, setData] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.locations.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    vals.full_name = `${vals.region} ${vals.city}`;
    if (editingId) { await masterAPI.locations.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.locations.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.locations.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  const regions = Array.from(new Set(data.map(d => d.region))).sort();

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">총 {data.length}개소 ({regions.length}개 지역)</Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>장소 등록</Button>
        </Space>
      </div>
      <Table dataSource={data} rowKey="id" size="small" loading={loading} pagination={false} showSorterTooltip={false}
        locale={{ emptyText: <EmptyState title="등록된 시험장소가 없습니다" size="sm" /> }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 50, sorter: (a: Location, b: Location) => a.id - b.id },
          { title: '지역', dataIndex: 'region', width: 80, sorter: (a: Location, b: Location) => a.region.localeCompare(b.region, 'ko'),
            render: (v: string) => <Tag color="blue">{v}</Tag>,
            filters: regions.map(r => ({ text: r, value: r })), onFilter: (v: any, r: Location) => r.region === v },
          { title: '시/군', dataIndex: 'city', width: 100, sorter: (a: Location, b: Location) => a.city.localeCompare(b.city, 'ko'), render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
          { title: '합성명', dataIndex: 'full_name', width: 130, sorter: (a: Location, b: Location) => a.full_name.localeCompare(b.full_name, 'ko') },
          { title: '상세주소', dataIndex: 'detail_address', ellipsis: true },
          { title: '상태', dataIndex: 'is_active', width: 70, sorter: (a: Location, b: Location) => a.is_active - b.is_active, render: (v: number) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
          { title: '', width: 80, render: (_: any, r: Location) => (
            <Space size={4}>
              <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
              <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
                <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
          )},
        ]}
      />
      <Modal title={editingId ? '장소 수정' : '장소 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.sm} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="region" label="지역" rules={[{ required: true }]}><Input placeholder="충남, 경기..." /></Form.Item></Col>
            <Col span={12}><Form.Item name="city" label="시/군" rules={[{ required: true }]}><Input placeholder="서산, 용인..." /></Form.Item></Col>
          </Row>
          <Form.Item name="detail_address" label="상세주소"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={18}><Form.Item name="note" label="비고"><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="is_active" label="상태" initialValue={1}><Select><Option value={1}>활성</Option><Option value={0}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 시험구분 탭 ──
const CategoryTab: React.FC = () => {
  const [data, setData] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.categories.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.categories.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.categories.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.categories.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">총 {data.length}개 구분</Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>구분 등록</Button>
        </Space>
      </div>
      <Table dataSource={data} rowKey="id" size="small" loading={loading} pagination={false} showSorterTooltip={false}
        locale={{ emptyText: <EmptyState title="등록된 시험구분이 없습니다" size="sm" /> }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 50, sorter: (a: Category, b: Category) => a.id - b.id },
          { title: '구분명', dataIndex: 'name', width: 180, sorter: (a: Category, b: Category) => a.name.localeCompare(b.name, 'ko'), render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
          { title: '설명', dataIndex: 'description', ellipsis: true },
          { title: '정렬', dataIndex: 'sort_order', width: 60, sorter: (a: Category, b: Category) => (a.sort_order || 0) - (b.sort_order || 0) },
          { title: '상태', dataIndex: 'is_active', width: 70, sorter: (a: Category, b: Category) => a.is_active - b.is_active, render: (v: number) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
          { title: '', width: 80, render: (_: any, r: Category) => (
            <Space size={4}>
              <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
              <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
                <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
          )},
        ]}
      />
      <Modal title={editingId ? '구분 수정' : '구분 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.sm} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="name" label="구분명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="설명"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="sort_order" label="정렬순서" initialValue={0}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="is_active" label="상태" initialValue={1}><Select><Option value={1}>활성</Option><Option value={0}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 포장주 탭 ──
const FieldOwnerTab: React.FC = () => {
  const [data, setData] = useState<FieldOwner[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [ownerRes, locRes] = await Promise.all([masterAPI.fieldOwners.list(), masterAPI.locations.list()]);
      setData(ownerRes.data); setLocations(locRes.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.fieldOwners.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.fieldOwners.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.fieldOwners.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">총 {data.length}명</Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>포장주 등록</Button>
        </Space>
      </div>
      <Table dataSource={data} rowKey="id" size="small" loading={loading} pagination={{ defaultPageSize: 20 }} showSorterTooltip={false}
        locale={{ emptyText: <EmptyState title="등록된 포장주가 없습니다" size="sm" /> }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 50, sorter: (a: FieldOwner, b: FieldOwner) => a.id - b.id },
          { title: '포장주명', dataIndex: 'name', width: 100, sorter: (a: FieldOwner, b: FieldOwner) => a.name.localeCompare(b.name, 'ko'), render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
          { title: '연락처', dataIndex: 'phone', width: 130, sorter: (a: FieldOwner, b: FieldOwner) => (a.phone || '').localeCompare(b.phone || '') },
          { title: '시험장소', dataIndex: 'location_name', width: 120, sorter: (a: FieldOwner, b: FieldOwner) => (a.location_name || '').localeCompare(b.location_name || '', 'ko'), render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-' },
          { title: '주소', dataIndex: 'address', ellipsis: true },
          { title: '상태', dataIndex: 'is_active', width: 70, sorter: (a: FieldOwner, b: FieldOwner) => a.is_active - b.is_active, render: (v: number) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
          { title: '', width: 80, render: (_: any, r: FieldOwner) => (
            <Space size={4}>
              <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
              <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
                <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
          )},
        ]}
      />
      <Modal title={editingId ? '포장주 수정' : '포장주 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.sm} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="포장주명" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="연락처"><Input placeholder="010-xxxx-xxxx" /></Form.Item></Col>
          </Row>
          <Form.Item name="location_id" label="시험장소">
            <Select allowClear showSearch placeholder="시험장소 선택"
              filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
              {locations.map(l => <Option key={l.id} value={l.id}>{l.full_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="address" label="포장 주소"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={18}><Form.Item name="note" label="비고"><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="is_active" label="상태" initialValue={1}><Select><Option value={1}>활성</Option><Option value={0}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 차량 탭 ──
const VehicleTab: React.FC = () => {
  const [data, setData] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.vehicles.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.vehicles.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.vehicles.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.vehicles.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">총 {data.length}대</Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>차량 등록</Button>
        </Space>
      </div>
      <Table dataSource={data} rowKey="id" size="small" loading={loading} pagination={false} showSorterTooltip={false}
        locale={{ emptyText: <EmptyState title="등록된 차량이 없습니다" size="sm" /> }}
        columns={[
          { title: '차량번호', dataIndex: 'vehicle_number', width: 120, sorter: (a: Vehicle, b: Vehicle) => a.vehicle_number.localeCompare(b.vehicle_number), render: (v: string) => <Typography.Text strong><CarOutlined style={{ marginRight: 4, color: '#4f46e5' }} />{v}</Typography.Text> },
          { title: '차종', dataIndex: 'vehicle_type', width: 100 },
          { title: '모델', dataIndex: 'model', width: 150 },
          { title: '담당자', dataIndex: 'owner', width: 100 },
          { title: '상태', dataIndex: 'is_active', width: 70, render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
          { title: '비고', dataIndex: 'note', ellipsis: true },
          { title: '', width: 80, render: (_: any, r: Vehicle) => (
            <Space size={4}>
              <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
              <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
                <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
          )},
        ]}
      />
      <Modal title={editingId ? '차량 수정' : '차량 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.sm} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="vehicle_number" label="차량번호" rules={[{ required: true }]}><Input placeholder="예: 3635" /></Form.Item></Col>
            <Col span={12}><Form.Item name="vehicle_type" label="차종"><Input placeholder="예: SUV, 승합" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={14}><Form.Item name="model" label="모델"><Input placeholder="예: 투싼, 카니발" /></Form.Item></Col>
            <Col span={10}><Form.Item name="owner" label="담당자"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={18}><Form.Item name="note" label="비고"><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="is_active" label="상태" initialValue={true}><Select><Option value={true}>활성</Option><Option value={false}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 연구원 탭 ──
const ResearcherTab: React.FC = () => {
  const [data, setData] = useState<Researcher[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.researchers.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.researchers.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.researchers.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.researchers.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = data.findIndex(r => r.id === active.id);
    const newIdx = data.findIndex(r => r.id === over.id);
    const reordered = arrayMove(data, oldIdx, newIdx).map((r, i) => ({ ...r, sort_order: i + 1 }));
    setData(reordered);
    try {
      await Promise.all(reordered.map(r => masterAPI.researchers.update(r.id, { ...r, sort_order: r.sort_order })));
      message.success('순서가 저장되었습니다.');
    } catch {
      message.error('순서 저장에 실패했습니다.');
      fetch();
    }
  };

  const SortableRow = ({ record, children, ...props }: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: record.id });
    return (
      <tr ref={setNodeRef} {...props} style={{
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f0f9ff' : undefined,
      }}>
        {React.Children.map(children, (child: any, idx) =>
          idx === 0
            ? React.cloneElement(child, {
                children: (
                  <span {...attributes} {...listeners} style={{ cursor: 'grab', color: gray[400], fontSize: 14 }}>
                    <HolderOutlined />
                  </span>
                ),
              })
            : child
        )}
      </tr>
    );
  };

  const columns = [
    { title: '', width: 36, render: () => null },
    { title: '이름', dataIndex: 'name', width: 100, render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
    { title: '직위', dataIndex: 'position', width: 100 },
    { title: '부서', dataIndex: 'department', width: 120 },
    { title: '연락처', dataIndex: 'phone', width: 130 },
    { title: '이메일', dataIndex: 'email', width: 200 },
    { title: '상태', dataIndex: 'is_active', width: 70, render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
    { title: '비고', dataIndex: 'note', ellipsis: true },
    { title: '', width: 80, render: (_: any, r: Researcher) => (
      <Space size={4}>
        <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
        <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
          <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Typography.Text type="secondary">총 {data.length}명</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>☰ 행을 드래그하여 순서를 변경하세요</Typography.Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>연구원 등록</Button>
        </Space>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <Table
            dataSource={data} rowKey="id" size="small" loading={loading} pagination={false}
            columns={columns}
            components={{ body: { row: (props: any) => {
              const record = data.find(r => r.id === props['data-row-key']);
              return record ? <SortableRow record={record} {...props} /> : <tr {...props} />;
            }}}}
          />
        </SortableContext>
      </DndContext>
      <Modal title={editingId ? '연구원 수정' : '연구원 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.md} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="이름" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="position" label="직위"><Input placeholder="예: 연구원, 선임연구원" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="department" label="부서"><Input placeholder="예: 약효시험팀" /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="연락처"><Input placeholder="010-0000-0000" /></Form.Item></Col>
          </Row>
          <Form.Item name="email" label="이메일"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="note" label="비고"><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="sort_order" label="정렬순서" initialValue={999}><InputNumber style={{ width: '100%' }} min={1} max={999} /></Form.Item></Col>
            <Col span={6}><Form.Item name="is_active" label="상태" initialValue={true}><Select><Option value={true}>활성</Option><Option value={false}>비활성</Option></Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── 상태코드 탭 ──
interface StatusCode { id: number; code: string; color?: string; sort_order?: number; description?: string; is_active?: boolean; }

const StatusCodeTab: React.FC = () => {
  const [data, setData] = useState<StatusCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setData((await masterAPI.statusCodes.list()).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    if (editingId) { await masterAPI.statusCodes.update(editingId, vals); message.success('수정되었습니다.'); }
    else { await masterAPI.statusCodes.create(vals); message.success('등록되었습니다.'); }
    setModalOpen(false); form.resetFields(); setEditingId(null); fetch();
  };
  const handleDelete = async (id: number) => {
    try { await masterAPI.statusCodes.delete(id); message.success('삭제되었습니다.'); fetch(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제 실패'); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = data.findIndex(r => r.id === active.id);
    const newIdx = data.findIndex(r => r.id === over.id);
    const reordered = arrayMove(data, oldIdx, newIdx).map((r, i) => ({ ...r, sort_order: i + 1 }));
    setData(reordered);
    try {
      await Promise.all(reordered.map(r => masterAPI.statusCodes.update(r.id, { ...r, sort_order: r.sort_order })));
      message.success('순서가 저장되었습니다.');
    } catch {
      message.error('순서 저장에 실패했습니다.');
      fetch();
    }
  };

  const SortableRow = ({ record, children, ...props }: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: record.id });
    return (
      <tr ref={setNodeRef} {...props} style={{
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f0f9ff' : undefined,
      }}>
        {React.Children.map(children, (child: any, idx) =>
          idx === 0
            ? React.cloneElement(child, {
                children: (
                  <span {...attributes} {...listeners} style={{ cursor: 'grab', color: gray[400], fontSize: 14 }}>
                    <HolderOutlined />
                  </span>
                ),
              })
            : child
        )}
      </tr>
    );
  };

  const columns = [
    { title: '', width: 36, render: () => null },
    { title: '진행상태', dataIndex: 'code', width: 120, render: (v: string, r: StatusCode) => (
      <Tag color={r.color || '#94a3b8'} style={{ fontWeight: 600, fontSize: 12 }}>{v}</Tag>
    )},
    { title: '색상', dataIndex: 'color', width: 120, render: (v: string) => v ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: v, border: '1px solid #e2e8f0' }} />
        <Typography.Text style={{ fontSize: 11 }}>{v}</Typography.Text>
      </span>
    ) : '-' },
    { title: '설명', dataIndex: 'description', ellipsis: true },
    { title: '상태', dataIndex: 'is_active', width: 80, render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={v ? '활성' : '비활성'} /> },
    { title: '', width: 72, render: (_: any, r: StatusCode) => (
      <Space size={4}>
        <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); form.setFieldsValue(r); setModalOpen(true); }} /></Tooltip>
        <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(r.id)} okText="삭제" cancelText="취소">
          <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Typography.Text type="secondary">총 {data.length}개</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>☰ 행을 드래그하여 순서를 변경하세요</Typography.Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetch} size="small" />
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>진행상태 등록</Button>
        </Space>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <Table
            dataSource={data} rowKey="id" size="small" loading={loading} pagination={false}
            columns={columns}
            components={{ body: { row: (props: any) => {
              const record = data.find(r => r.id === props['data-row-key']);
              return record ? <SortableRow record={record} {...props} /> : <tr {...props} />;
            }}}}
          />
        </SortableContext>
      </DndContext>
      <Modal title={editingId ? '진행상태 수정' : '진행상태 등록'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); }} okText="저장" cancelText="취소" width={modalWidth.sm} maskClosable={false}>
        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={14}><Form.Item name="code" label="진행상태" rules={[{ required: true, message: '진행상태를 입력하세요' }]}><Input placeholder="예: 처리완료, 성적서완료" /></Form.Item></Col>
            <Col span={10}><Form.Item name="sort_order" label="정렬순서" initialValue={999}><InputNumber style={{ width: '100%' }} min={1} max={999} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="color" label="색상 코드" getValueFromEvent={(c) => c.toHexString()}>
                <ColorPicker
                  format="hex"
                  showText
                  presets={[{
                    label: '기본 상태색',
                    colors: ['#94a3b8', '#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#0ea5e9', '#64748b'],
                  }]}
                />
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item name="is_active" label="상태" initialValue={true}><Select><Option value={true}>활성</Option><Option value={false}>비활성</Option></Select></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="설명"><Input /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ── 메인 마스터 페이지 ──
const MasterPage: React.FC = () => {
  const [counts, setCounts] = useState({ companies: 0, locations: 0, categories: 0, owners: 0, vehicles: 0, researchers: 0 });

  useEffect(() => {
    Promise.all([
      masterAPI.companies.list(), masterAPI.locations.list(),
      masterAPI.categories.list(), masterAPI.fieldOwners.list(),
      masterAPI.vehicles.list(), masterAPI.researchers.list(),
    ]).then(([c, l, cat, o, v, r]) => {
      setCounts({
        companies: c.data.length, locations: l.data.length,
        categories: cat.data.length, owners: o.data.length,
        vehicles: v.data.length, researchers: r.data.length,
      });
    });
  }, []);

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="의뢰사" value={counts.companies} prefix={<BankOutlined style={{ color: brand.primary }} />} suffix="개사" /></Card></Col>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="시험장소" value={counts.locations} prefix={<EnvironmentOutlined style={{ color: semantic.iconBlue }} />} suffix="개소" /></Card></Col>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="시험구분" value={counts.categories} prefix={<TagsOutlined style={{ color: semantic.iconGreen }} />} suffix="개" /></Card></Col>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="포장주" value={counts.owners} prefix={<UserOutlined style={{ color: semantic.iconAmber }} />} suffix="명" /></Card></Col>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="차량" value={counts.vehicles} prefix={<CarOutlined style={{ color: semantic.iconRed }} />} suffix="대" /></Card></Col>
        <Col xs={12} sm={4}><Card size="small"><Statistic title="연구원" value={counts.researchers} prefix={<TeamOutlined style={{ color: semantic.iconPurple }} />} suffix="명" /></Card></Col>
      </Row>

      <Card>
        <Tabs items={[
          { key: 'companies', label: <span><BankOutlined /> 의뢰사</span>, children: <CompanyTab /> },
          { key: 'locations', label: <span><EnvironmentOutlined /> 시험장소</span>, children: <LocationTab /> },
          { key: 'categories', label: <span><TagsOutlined /> 시험구분</span>, children: <CategoryTab /> },
          { key: 'owners', label: <span><UserOutlined /> 포장주</span>, children: <FieldOwnerTab /> },
          { key: 'vehicles', label: <span><CarOutlined /> 차량</span>, children: <VehicleTab /> },
          { key: 'researchers', label: <span><TeamOutlined /> 연구원</span>, children: <ResearcherTab /> },
          { key: 'statusCodes', label: <span><TagsOutlined /> 진행상태</span>, children: <StatusCodeTab /> },
        ]} />
      </Card>
    </div>
  );
};

export default MasterPage;
